import { useParams, useLocation } from "react-router-dom";
import { useState, useMemo, useRef, useEffect } from "react";
import { Breadcrumb, BreadcrumbItem, Alert, Modal, Tooltip } from "flowbite-react";
import { HiHome, HiExclamationCircle, HiInformationCircle } from "react-icons/hi";
import { useTranslation, useAuthentication } from "@repo/core";
import type { StrategyTimeline } from "../../types/strategy-detail";

// Import components
import StrategyHeader from "./header/StrategyHeader";
import StrategySettings from "./settings/StrategySettings";
import { PerformanceDetailsComponent as PerformanceDetails } from "./performance/PerformanceDetails";
import { PortfolioConstituents } from "./constituents";
import { ReasoningChat } from "./reasoning";
import {
  usePredeliveredChartData,
  usePredeliveredTableData,
  useStrategyWithDraft,
} from "@repo/api";
import DrawdownsChart from "./drawdown/DrawdownsChart";
import TurnoverChart from "./turnover/TurnoverChart";
import { usePortfolioConstituents } from "../../hooks/indicators/usePortfolioConstituents";
import { useDrawdown } from "../../hooks/indicators/useDrawdown";
import { useTurnover } from "../../hooks/indicators/useTurnover";
import { useStrategyDetailType } from "../../hooks/useStrategyDetailType";
import { useBenchmarks } from "../../hooks/api/useUniversesApi";
import MonthlyReturnsAnalysisComponent from "./returns/MonthlyReturnsAnalysis";
import { formatPercentage } from "../../utils";
import { t } from "i18next";

interface StrategyDetailProps {
  className?: string;
}

const StrategyDetail = ({ className = "" }: StrategyDetailProps) => {
  const { dateRange } = useParams<{
    strategyId: string;
    dateRange: string;
  }>();
  const location = useLocation();

  // Check if we're in draft mode by looking at the URL path
  const isDraftMode = location.pathname.endsWith("/draft");

  const { tNav, tStrategy, tCommon } = useTranslation();
  const { user } = useAuthentication();
  const customStrategy = useStrategyWithDraft();
  const { isDelivered, isActive } = useStrategyDetailType(customStrategy.data);
  // Reasoning exists for delivered templates and saved user portfolios, not for drafts
  const showReasoning =
    (user?.permissions.includes("Factor_reasoning_access") ?? false) && (isDelivered || isActive);
  const reasoningVariant = isDelivered ? "template" : "user";
  const portfolioConstituents = usePortfolioConstituents();
  const drawdownData = useDrawdown();
  const turnoverData = useTurnover();
  const chartData = usePredeliveredChartData();
  const benchmarkQuery = useBenchmarks();

  const tableData = usePredeliveredTableData();

  const portfolio = customStrategy.data;

  // Track selected benchmark from header chart
  const [selectedBenchmark, setSelectedBenchmark] = useState<string>("SP500");

  // Get benchmark display name
  const benchmarkDisplayName = useMemo(() => {
    if (benchmarkQuery.data && typeof benchmarkQuery.data === 'object' && !Array.isArray(benchmarkQuery.data)) {
      const benchmarkData = benchmarkQuery.data as Record<string, { name: string; type: string; universe: string }>;
      if (benchmarkData[selectedBenchmark]) {
        return benchmarkData[selectedBenchmark].name || selectedBenchmark;
      }
      // Try to find by universe match
      const matchingBenchmark = Object.entries(benchmarkData).find(
        ([, value]) => value.universe?.toUpperCase() === selectedBenchmark
      );
      if (matchingBenchmark) {
        return matchingBenchmark[1].name || selectedBenchmark;
      }
    }
    return selectedBenchmark;
  }, [selectedBenchmark, benchmarkQuery.data]);

  // Modal state for Strategy Settings on small screens
  const [isStrategySettingsModalOpen, setIsStrategySettingsModalOpen] =
    useState(false);

  // Ref and state to sync ReasoningChat height with PortfolioConstituents
  const portfolioConstituentsRef = useRef<HTMLDivElement>(null);
  const [constituentsHeight, setConstituentsHeight] = useState<number | undefined>(undefined);
  const [selectedRebalanceDate, setSelectedRebalanceDate] = useState<string | undefined>(undefined);

  // Update constituents height when the ref element changes size
  useEffect(() => {
    const element = portfolioConstituentsRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setConstituentsHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // Helper functions to detect empty data states
  const isEmptyData = (data: unknown): boolean => {
    if (!data) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    if (typeof data === "object" && Object.keys(data).length === 0) return true;
    return false;
  };

  const isStrategyDataEmpty = isEmptyData(customStrategy.data);
  const isDrawdownDataEmpty = isEmptyData(drawdownData.data);
  const isTurnoverDataEmpty = isEmptyData(turnoverData.data);
  const isChartDataEmpty = isEmptyData(chartData.data);
  const isTableDataEmpty = isEmptyData(tableData.data);
  const isPortfolioDataEmpty = isEmptyData(portfolioConstituents.data);

  // Per-dataset failures. Kept separate from "no data yet": a query that has
  // given up must stop reporting as loading, otherwise the empty-data fallback
  // below turns every permanent failure into an endless spinner and the child
  // components' own missing-data states are never reached.
  const isStrategyError = customStrategy.isError;
  const isDrawdownError = drawdownData.isError;
  const isTurnoverError = !!turnoverData.error;
  const isChartError = chartData.isError;
  const isTableError = tableData.isError;
  const isPortfolioError = portfolioConstituents.isError;

  // Enhanced loading states that consider empty data as still loading - but
  // only while the query can still deliver. Once it has errored, empty data
  // means "failed", not "pending".
  const isStrategyLoading =
    !isStrategyError && (customStrategy.isLoading || isStrategyDataEmpty);
  const isDrawdownLoading =
    !isDrawdownError && (drawdownData.isLoading || isDrawdownDataEmpty);
  const isTurnoverLoading =
    !isTurnoverError && (turnoverData.isLoading || isTurnoverDataEmpty);
  const isChartLoading =
    !isChartError && (chartData.isLoading || isChartDataEmpty);
  const isTableLoading =
    !isTableError && (tableData.isLoading || isTableDataEmpty);
  const isPortfolioLoading =
    !isPortfolioError &&
    (portfolioConstituents.isLoading || isPortfolioDataEmpty);

  // Any failed dataset is worth surfacing, not just the strategy metadata -
  // otherwise a single broken chart is indistinguishable from one with no data.
  const hasDataError =
    isStrategyError ||
    isDrawdownError ||
    isTurnoverError ||
    isChartError ||
    isTableError ||
    isPortfolioError;

  // Handle error states
  const hasError = customStrategy.isError;

  // Create timeline data from strategy data (hardcoded for now)
  const timelineData: StrategyTimeline | null = useMemo(() => {
    if (!portfolio) return null;

    // Missing timeline data in Strategy type - using hardcoded empty data
    return {
      strategy: [], // Missing in Strategy type
      benchmark: [], // Missing in Strategy type
      strategyName: portfolio.name,
      benchmarkName: "Benchmark", // Missing in Strategy type - hardcoded
    };
  }, [portfolio]);

  // Create performance details from table data
  const performanceDetails = useMemo(() => {
    if (!tableData.data) return null;

    const data = tableData.data;

    // Helper function to format numbers with fallback
    const formatNumber = (value: number | undefined, decimals = 2): string => {
      if (value === undefined || Number.isNaN(value)) return "—";
      return value.toFixed(decimals);
    };

    // Arithmetic on a missing metric (e.g. `undefined * 100`) produces NaN,
    // not undefined, which would slip past the table's `!== undefined` check
    // and render "NaN%" instead of the empty-cell fallback. Guard first.
    const safeAbs = (value: number | undefined): number | undefined =>
      value === undefined ? undefined : Math.abs(value);
    const safePercent = (value: number | undefined): number | undefined =>
      value === undefined ? undefined : value * 100;

    // Transform table data into performance metrics format

    // Get benchmark data dynamically based on selection
    // If benchmark doesn't exist in data, leave it undefined (cells will show "—")
    const benchmarkData = data[selectedBenchmark];

    const metrics = [
      {
        name: tStrategy("performanceMetrics.totalReturn.name"),
        displayConversion: (value: number) =>
          `${value > 0 ? "+" : ""}${formatPercentage(value)}`,
        tooltipConversion: (value: number) =>
          `${value > 0 ? "+" : ""} ${value * 100}%`,
        description: tStrategy("performanceMetrics.totalReturn.description"),
        strategy: data.Strategy?.["Final-Equity"] ? data.Strategy["Final-Equity"] - 1 : undefined,

        benchmark: benchmarkData?.["Final-Equity"] ? benchmarkData["Final-Equity"] - 1 : undefined,
        universe: data.Universe?.["Final-Equity"] ? data.Universe["Final-Equity"] - 1 : undefined,
        longLeg: data.LongOnly?.["Final-Equity"] ? data.LongOnly["Final-Equity"] - 1 : undefined,
        shortLeg: data.LongTheShort?.["Final-Equity"] ? data.LongTheShort["Final-Equity"] - 1 : undefined,
        shortShort: data.LongShort?.["Final-Equity"] ? data.LongShort["Final-Equity"] - 1 : undefined,
      },
      {
        name: tStrategy("performanceMetrics.cagr.name"),
        description: tStrategy("performanceMetrics.cagr.description"),
        displayConversion: (value: number) =>
          `${value > 0 ? "+" : ""}${formatPercentage(value)}`,
        tooltipConversion: (value: number) =>
          `${value > 0 ? "+" : ""} ${value * 100}%`,
        strategy: data.Strategy?.CAGR,
        benchmark: benchmarkData?.CAGR,
        universe: data.Universe?.CAGR,
        longLeg: data.LongOnly?.CAGR,
        shortLeg: data.LongTheShort?.CAGR,
        shortShort: data.LongShort?.CAGR,
      },
      {
        name: tStrategy("performanceMetrics.annualVolatility.name"),
        description: tStrategy("performanceMetrics.annualVolatility.description"),
        displayConversion: (value: number) => formatPercentage(value),
        tooltipConversion: (value: number) => `${value * 100}%`,
        strategy: data.Strategy?.Volatility,
        benchmark: benchmarkData?.Volatility,
        universe: data.Universe?.Volatility,
        longLeg: data.LongOnly?.Volatility,
        shortLeg: data.LongTheShort?.Volatility,
        shortShort: data.LongShort?.Volatility,
      },
      {
        name: tStrategy("performanceMetrics.sharpeRatio.name"),
        description: tStrategy("performanceMetrics.sharpeRatio.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Sharpe,
        benchmark: benchmarkData?.Sharpe,
        universe: data.Universe?.Sharpe,
        longLeg: data.LongOnly?.Sharpe,
        shortLeg: data.LongTheShort?.Sharpe,
        shortShort: data.LongShort?.Sharpe,
      },
      {
        name: tStrategy("performanceMetrics.maxDrawdown.name"),
        description: tStrategy("performanceMetrics.maxDrawdown.description"),
        displayConversion: (value: number) => formatPercentage(value),
        tooltipConversion: (value: number) => `${value * 100}%`,
        strategy: data.Strategy?.["Max-Drawdown"],
        benchmark: benchmarkData?.["Max-Drawdown"],
        universe: data.Universe?.["Max-Drawdown"],
        longLeg: data.LongOnly?.["Max-Drawdown"],
        shortLeg: data.LongTheShort?.["Max-Drawdown"],
        shortShort: data.LongShort?.["Max-Drawdown"],
      },
      {
        name: tStrategy("performanceMetrics.alphaUniverse.name"),
        description: tStrategy("performanceMetrics.alphaUniverse.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: safePercent(data.Strategy?.["Alpha-Universe"]),
        benchmark: safePercent(benchmarkData?.["Alpha-Universe"]),
        universe: safePercent(data.Universe?.["Alpha-Universe"]),
        longLeg: safePercent(data.LongOnly?.["Alpha-Universe"]),
        shortLeg: safePercent(data.LongTheShort?.["Alpha-Universe"]),
        shortShort: safePercent(data.LongShort?.["Alpha-Universe"]),
      },
      {
        name: tStrategy("performanceMetrics.betaUniverse.name"),
        description: tStrategy("performanceMetrics.betaUniverse.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.["Beta-Universe"],
        benchmark: benchmarkData?.["Beta-Universe"],
        universe: data.Universe?.["Beta-Universe"],
        longLeg: data.LongOnly?.["Beta-Universe"],
        shortLeg: data.LongTheShort?.["Beta-Universe"],
        shortShort: data.LongShort?.["Beta-Universe"],
      },
      {
        name: tStrategy("performanceMetrics.alphaSP500.name"),
        description: tStrategy("performanceMetrics.alphaSP500.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: safePercent(data.Strategy?.["Alpha-SP500"]),
        benchmark: safePercent(benchmarkData?.["Alpha-SP500"]),
        universe: safePercent(data.Universe?.["Alpha-SP500"]),
        longLeg: safePercent(data.LongOnly?.["Alpha-SP500"]),
        shortLeg: safePercent(data.LongTheShort?.["Alpha-SP500"]),
        shortShort: safePercent(data.LongShort?.["Alpha-SP500"]),
      },
      {
        name: tStrategy("performanceMetrics.betaSP500.name"),
        description: tStrategy("performanceMetrics.betaSP500.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.["Beta-SP500"],
        benchmark: benchmarkData?.["Beta-SP500"],
        universe: data.Universe?.["Beta-SP500"],
        longLeg: data.LongOnly?.["Beta-SP500"],
        shortLeg: data.LongTheShort?.["Beta-SP500"],
        shortShort: data.LongShort?.["Beta-SP500"],
      },
      {
        name: tStrategy("performanceMetrics.sterlingRatio.name"),
        description: tStrategy("performanceMetrics.sterlingRatio.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Sterling,
        benchmark: benchmarkData?.Sterling,
        universe: data.Universe?.Sterling,
        longLeg: data.LongOnly?.Sterling,
        shortLeg: data.LongTheShort?.Sterling,
        shortShort: data.LongShort?.Sterling,
      },
      {
        name: tStrategy("performanceMetrics.sortinoRatio.name"),
        description: tStrategy("performanceMetrics.sortinoRatio.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Sortino,
        benchmark: benchmarkData?.Sortino,
        universe: data.Universe?.Sortino,
        longLeg: data.LongOnly?.Sortino,
        shortLeg: data.LongTheShort?.Sortino,
        shortShort: data.LongShort?.Sortino,
      },
      {
        name: tStrategy("performanceMetrics.calmarRatio.name"),
        description: tStrategy("performanceMetrics.calmarRatio.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Calmar,
        benchmark: benchmarkData?.Calmar,
        universe: data.Universe?.Calmar,
        longLeg: data.LongOnly?.Calmar,
        shortLeg: data.LongTheShort?.Calmar,
        shortShort: data.LongShort?.Calmar,
      },
      {
        name: tStrategy("performanceMetrics.winRate.name"),
        description: tStrategy("performanceMetrics.winRate.description"),
        displayConversion: (value: number) => formatPercentage(value),
        tooltipConversion: (value: number) => value * 100 + "%",
        strategy: data.Strategy?.["Win-Rate"],
        benchmark: benchmarkData?.["Win-Rate"],
        universe: data.Universe?.["Win-Rate"],
        longLeg: data.LongOnly?.["Win-Rate"],
        shortLeg: data.LongTheShort?.["Win-Rate"],
        shortShort: data.LongShort?.["Win-Rate"],
      },
      {
        name: tStrategy("performanceMetrics.valueAtRisk.name"),
        description: tStrategy("performanceMetrics.valueAtRisk.description"),
        displayConversion: (value: number) => formatPercentage(value),
        tooltipConversion: (value: number) => value * 100 + "%",
        strategy: safeAbs(data.Strategy?.VaR95),
        benchmark: safeAbs(benchmarkData?.VaR95),
        universe: safeAbs(data.Universe?.VaR95),
        longLeg: safeAbs(data.LongOnly?.VaR95),
        shortLeg: safeAbs(data.LongTheShort?.VaR95),
        shortShort: safeAbs(data.LongShort?.VaR95),
      },
      {
        name: tStrategy("performanceMetrics.conditionalValueAtRisk.name"),
        description: tStrategy("performanceMetrics.conditionalValueAtRisk.description"),
        displayConversion: (value: number) => formatPercentage(value),
        tooltipConversion: (value: number) => value * 100 + "%",
        strategy: safeAbs(data.Strategy?.CVaR95),
        benchmark: safeAbs(benchmarkData?.CVaR95),
        universe: safeAbs(data.Universe?.CVaR95),
        longLeg: safeAbs(data.LongOnly?.CVaR95),
        shortLeg: safeAbs(data.LongTheShort?.CVaR95),
        shortShort: safeAbs(data.LongShort?.CVaR95),
      },
      {
        name: tStrategy("performanceMetrics.skewness.name"),
        description: tStrategy("performanceMetrics.skewness.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Skewness,
        benchmark: benchmarkData?.Skewness,
        universe: data.Universe?.Skewness,
        longLeg: data.LongOnly?.Skewness,
        shortLeg: data.LongTheShort?.Skewness,
        shortShort: data.LongShort?.Skewness,
      },
      {
        name: tStrategy("performanceMetrics.kurtosis.name"),
        description: tStrategy("performanceMetrics.kurtosis.description"),
        displayConversion: (value: number) =>
          `${formatNumber(value)}\u00A0\u00A0`,
        tooltipConversion: (value: number) => value.toString(),
        strategy: data.Strategy?.Kurtosis,
        benchmark: benchmarkData?.Kurtosis,
        universe: data.Universe?.Kurtosis,
        longLeg: data.LongOnly?.Kurtosis,
        shortLeg: data.LongTheShort?.Kurtosis,
        shortShort: data.LongShort?.Kurtosis,
      },
    ];

    return { metrics };
  }, [tableData.data, selectedBenchmark]);

  // Combine long and short constituents
  const isTimelineLoading = false; // No real timeline data yet
  const isBacktestInProgress = portfolio?.backtest?.backtestInProgress || false;

  // Bailing out early has to happen below every hook - an early return placed
  // above them would render fewer hooks than the previous pass and crash React.
  if (hasError) {
    console.error("Error loading strategy data:", customStrategy.error);
    if (customStrategy.error?.isServiceOverloaded) {
      return <div>Service is currently overwhelmed. Please try again later.</div>;
    }
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Content wrapper with left and right margins */}
      <div className="ml-6 lg:ml-12 mr-6 lg:mr-12">
        {/* Breadcrumb */}
        <div className="flex items-center justify-between mb-4 mt-4">
          <Breadcrumb aria-label="Strategy Detail Breadcrumb">
            <BreadcrumbItem href="/" icon={HiHome}>
              {tNav("overview")}
            </BreadcrumbItem>
            {dateRange && (
              <BreadcrumbItem href="/factor-mimicking">
                {tNav("indicatorMimicking")}
              </BreadcrumbItem>
            )}
            <BreadcrumbItem
              href={`/${portfolio?.modelId.replace("_draft", "")}`}
            >
              {tNav("indicatorDetail")}
            </BreadcrumbItem>
            {isDraftMode && (
              <BreadcrumbItem
                href={`/${portfolio?.modelId.replace("_draft", "")}/draft`}
              >
                {tCommon("draft")}
              </BreadcrumbItem>
            )}
          </Breadcrumb>
        </div>

        {/* Error State */}
        {hasDataError && (
          <Alert color="failure" icon={HiExclamationCircle} className="mb-4">
            <span className="font-medium">{tStrategy("errorLoadingData")}</span>
            <div className="mt-1">{tStrategy("errorLoadingDataDetails")}</div>
          </Alert>
        )}

        {/* Main Content */}
        <div className="space-y-6">
          {/* Two-column layout: Left (8-col) and Right (4-col) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column: Strategy Header + Portfolio Constituents */}
            <div className="lg:col-span-8 space-y-6">
              <StrategyHeader
                dateRange={dateRange}
                portfolio={portfolio}
                timeline={timelineData}
                chartData={chartData.data}
                isStrategyLoading={isStrategyLoading || isBacktestInProgress}
                isTimelineLoading={isTimelineLoading || isBacktestInProgress}
                isChartDataLoading={isChartLoading || isBacktestInProgress}
                onOpenStrategySettings={() =>
                  setIsStrategySettingsModalOpen(true)
                }
                onBenchmarkChange={setSelectedBenchmark}
              />

              <div ref={portfolioConstituentsRef}>
                <PortfolioConstituents
                  strategy={portfolio}
                  portfolio={portfolioConstituents.data}
                  isLoading={isPortfolioLoading || isBacktestInProgress}
                  onDateChange={setSelectedRebalanceDate}
                />
              </div>

              {/* Reasoning Chat - Visible on small screens only, gated by Factor_reasoning_access permission and strategy type */}
              {showReasoning && (
                <div className="lg:hidden">
                  <ReasoningChat
                    variant={reasoningVariant}
                    userId={portfolio?.userId}
                    strategyId={portfolio?.strategyId}
                    modelId={portfolio?.modelId}
                    period={portfolio?.backtest?.period}
                    rebalanceDate={selectedRebalanceDate}
                    isLoading={isPortfolioLoading || isBacktestInProgress}
                  />
                </div>
              )}
            </div>

            {/* Right Column: Strategy Settings + Reasoning Chat */}
            <div className="hidden lg:block lg:col-span-4 space-y-6">
              {/* Strategy Settings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex  mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {tStrategy("settings.title")}
                  </h3>
                  <div style={{
                    alignContent: "center"
                  }}>
                    <Tooltip
                      content={
                        <div className="min-w-[350px]">
                          <p>{t("settingsUI.myStrategyTooltip.line1")}</p>
                          <p className="mt-2">{t("settingsUI.myStrategyTooltip.line2")}</p>
                          <p className="mt-2">{t("settingsUI.myStrategyTooltip.line3")}</p>
                          <p className="mt-2">{t("settingsUI.myStrategyTooltip.line4")}</p>
                          <p className="mt-2">{t("settingsUI.myStrategyTooltip.line5")}</p>
                        </div>
                      }
                      placement="auto"
                    >
                      <HiInformationCircle className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help" />
                    </Tooltip>
                  </div>
                </div>
                {portfolio ? (
                  <StrategySettings
                    strategy={portfolio}
                    dateRange={dateRange}
                  />
                ) : (
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                )}
              </div>

              {/* Reasoning Chat - gated by Factor_reasoning_access permission and strategy type */}
              {showReasoning && (
                <ReasoningChat
                  variant={reasoningVariant}
                  userId={portfolio?.userId}
                  strategyId={portfolio?.strategyId}
                  modelId={portfolio?.modelId}
                  period={portfolio?.backtest?.period}
                  rebalanceDate={selectedRebalanceDate}
                  isLoading={isPortfolioLoading || isBacktestInProgress}
                  maxHeight={constituentsHeight}
                />
              )}
            </div>
          </div>

          {/* Full-width charts below */}
          <div className="col-span-12 space-y-6">

            {/* Drawdowns Chart */}
            <DrawdownsChart
              drawdowns={drawdownData.data}
              isLoading={isDrawdownLoading || isBacktestInProgress}
            />

            {/* Turnover Chart */}
            <TurnoverChart
              turnovers={turnoverData.data}
              meanTurnover={turnoverData.meanTurnover}
              statistics={turnoverData.statistics}
              isLoading={isTurnoverLoading || isBacktestInProgress}
            />

            {/* Monthly Returns Analysis */}
            <MonthlyReturnsAnalysisComponent isLoading={isBacktestInProgress} />

            {/* Performance Details Table */}
            <PerformanceDetails
              performance={performanceDetails}
              isLoading={isTableLoading || isBacktestInProgress}
              benchmarkName={benchmarkDisplayName}
            />
          </div>
        </div>
      </div>{" "}
      {/* Close content wrapper with left margin */}
      {/* Strategy Settings Modal - Visible only on small screens */}
      <Modal
        show={isStrategySettingsModalOpen}
        onClose={() => setIsStrategySettingsModalOpen(false)}
        size="lg"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {tNav("strategySettings")}
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setIsStrategySettingsModalOpen(false)}
            >
              <span className="sr-only">{tCommon("close")}</span>
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {portfolio ? (
            <StrategySettings strategy={portfolio} dateRange={dateRange} />
          ) : (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default StrategyDetail;
